import { Button } from "@ronin/ui";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { api, authStatusQuery } from "../api.js";
import { AuthForm } from "../components/auth-form.js";
import { ConfirmDialog } from "../components/notes/dialogs.js";
import { useConfirm } from "../lib/use-confirm.js";
import { isNative } from "../platform/native.js";
import { storedAccount } from "../platform/offline/index.js";
import { serverUrl } from "../platform/server.js";

export function LoginPage() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const confirm = useConfirm();

  return (
    <>
      <AuthForm
        title="Welcome back"
        description="Sign in to continue to Ronin."
        submitLabel="Sign in"
        fields={[
          { name: "username", label: "Username", type: "text", autoComplete: "username" },
          {
            name: "password",
            label: "Password",
            type: "password",
            autoComplete: "current-password",
          },
        ]}
        onSubmit={async ({ username = "", password = "" }) => {
          const user = await api.auth.signIn({ username, password });
          // spec11 FR17: another account's unsent offline notes are deleted, so ask first.
          const stored = isNative ? await storedAccount() : null;
          if (stored && stored.userId !== user.id && stored.pending > 0 && !(await confirm.ask())) {
            await api.auth.signOut();
            return;
          }
          // Drop the cached state so the route guards fetch the new session.
          queryClient.removeQueries({ queryKey: authStatusQuery.queryKey });
          await navigate({ to: "/todos" });
        }}
        footer={isNative && <ServerFooter />}
      />
      <ConfirmDialog
        open={confirm.open}
        title="Sign in as another account?"
        description="Unsynced notes of the previous account on this device will be deleted."
        confirmLabel="Delete and sign in"
        onClose={() => confirm.reply(false)}
        onConfirm={() => confirm.reply(true)}
      />
    </>
  );
}

/** spec08 FR5: which server the app signs in to, and the way to another one. */
export function ServerFooter() {
  const navigate = useNavigate();
  return (
    <div className="flex items-center justify-between gap-3 border-t pt-4">
      <span className="min-w-0 truncate text-xs text-muted-foreground">
        {new URL(serverUrl()).host}
      </span>
      <Button variant="ghost" size="sm" onClick={() => void navigate({ to: "/connect" })}>
        Change server
      </Button>
    </div>
  );
}
